<template>
  <div class="pi-export" @keydown.esc="closeMenu(true)">
    <button :id="ids.button" ref="toggle" type="button" class="pi-btn pi-btn--ghost pi-export__btn" aria-haspopup="true"
            :aria-expanded="open ? 'true' : 'false'" :aria-controls="ids.menu" :aria-label="buttonLabel" :aria-busy="busy ? 'true' : 'false'"
            :aria-disabled="busy ? 'true' : null" :disabled="disabled" @click="toggleMenu" @keydown.down.prevent="openMenu(0)"
            @keydown.up.prevent="openMenu(formats.length - 1)">
      <template v-if="busy">Exportando…</template>
      <template v-else>Exportar<span aria-hidden="true"> ▾</span></template>
    </button>
    <ul v-show="open" :id="ids.menu" class="pi-export__menu" role="menu" :aria-labelledby="ids.button">
      <li v-for="(format, index) in formats" :key="format.value" role="none">
        <button ref="items" type="button" role="menuitem" tabindex="-1" class="pi-export__item" @click="choose(format.value)"
                @keydown.down.prevent="focusItem(index + 1)" @keydown.up.prevent="focusItem(index - 1)"
                @keydown.home.prevent="focusItem(0)" @keydown.end.prevent="focusItem(formats.length - 1)" @keydown.tab="closeMenu(false)">
          {{ format.label }}
        </button>
      </li>
    </ul>
    <span class="pi-export__notice" :class="{ 'pi-sr': !statusText }" role="status" aria-live="polite">{{ statusText }}</span>
    <span class="pi-export__notice is-error" :class="{ 'pi-sr': !alertText }" role="alert">{{ alertText }}</span>
  </div>
</template>

<script>
import swal from "sweetalert";
import { fetchAllRows } from "./piApi.js";
import {
  EXPORT_FORMATS, EXPORT_REGISTRY_KEY, browserLink, browserUserName, exportErrorMessage, exportNotice, exportTable, inlinePage, loadedPage,
  mappedPage, partialQuestion,
} from "./piExport.js";

const NOTICE_MS = 8000;

let sequence = 0;

const confirmDialog = (title, text, accept) => swal({ title: title, text: text, buttons: ["Cancelar", accept] }).then((value) => value === true);

export default {
  name: "pi-export-menu",
  inject: {
    exportRegistry: { from: EXPORT_REGISTRY_KEY, default: null },
  },
  props: {
    title: { type: String, required: true },
    screen: { type: String, required: true },
    columns: { type: Array, required: true },
    rows: { type: Array, default: null },
    envelope: { type: Object, default: null },
    total: { type: Number, default: null },
    source: { type: Object, default: null },
    load: { type: Function, default: null },
    filters: { type: Array, default: () => [] },
    period: { type: Object, default: null },
    confirmText: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    register: { type: Boolean, default: true },
  },
  data() {
    sequence += 1;
    return {
      ids: { button: "pi-export-" + sequence, menu: "pi-export-menu-" + sequence },
      formats: EXPORT_FORMATS,
      open: false,
      busy: false,
      notice: "",
      noticeError: false,
    };
  },
  computed: {
    buttonLabel() {
      return (this.busy ? "Exportando " : "Exportar ") + this.title;
    },
    statusText() {
      return this.noticeError ? "" : this.notice;
    },
    alertText() {
      return this.noticeError ? this.notice : "";
    },
  },
  mounted() {
    if (this.register && this.exportRegistry) {
      this.leaveRegistry = this.exportRegistry.register({
        el: this.$el,
        title: () => this.title,
        confirm: () => this.confirmText,
        disabled: () => this.disabled,
        load: () => this.collectTable(),
      });
    }
  },
  beforeDestroy() {
    if (this.leaveRegistry) this.leaveRegistry();
    this.unlisten();
    this.clearTimer();
  },
  methods: {
    toggleMenu() {
      if (this.busy) return;
      if (this.open) this.closeMenu(true);
      else this.openMenu(0);
    },
    openMenu(index) {
      if (this.busy || this.disabled) return;
      this.open = true;
      this.listen();
      this.$nextTick(() => this.focusItem(index));
    },
    closeMenu(returnFocus) {
      if (!this.open) return;
      this.open = false;
      this.unlisten();
      if (returnFocus && this.$refs.toggle) this.$refs.toggle.focus();
    },
    focusItem(index) {
      const items = this.$refs.items || [];
      if (!items.length) return;
      const target = items[(index + items.length) % items.length];
      if (target) target.focus();
    },
    listen() {
      if (this.listening) return;
      this.listening = true;
      document.addEventListener("mousedown", this.onOutside, true);
    },
    unlisten() {
      if (!this.listening) return;
      this.listening = false;
      document.removeEventListener("mousedown", this.onOutside, true);
    },
    onOutside(event) {
      if (this.$el && !this.$el.contains(event.target)) this.closeMenu(false);
    },
    clearTimer() {
      if (this.noticeTimer) clearTimeout(this.noticeTimer);
      this.noticeTimer = null;
    },
    showNotice(text, error) {
      this.clearTimer();
      this.notice = text;
      this.noticeError = error === true;
      if (!this.noticeError) {
        this.noticeTimer = setTimeout(() => {
          this.notice = "";
          this.noticeTimer = null;
        }, NOTICE_MS);
      }
    },
    choose(format) {
      this.closeMenu(true);
      this.run(format);
    },
    collect() {
      if (typeof this.load === "function") return Promise.resolve().then(() => this.load()).then(loadedPage);
      if (this.source && this.source.path) {
        return fetchAllRows(this.source.path, this.source.query, this.source.pick).then((page) => mappedPage(page, this.source.map));
      }
      return Promise.resolve(inlinePage(this.rows, this.envelope, this.total));
    },
    collectTable() {
      return this.collect().then((page) => ({
        title: this.title,
        columns: this.columns,
        rows: page.rows,
        envelope: page.envelope,
        total: page.total,
        exportedRows: page.exportedRows,
        truncated: page.truncated,
      }));
    },
    confirmStart() {
      if (!this.confirmText) return Promise.resolve(true);
      return confirmDialog("Confirmar exportação", this.confirmText, "Exportar");
    },
    confirmPartial(page) {
      if (!page.truncated) return Promise.resolve(true);
      return confirmDialog("Exportação parcial", partialQuestion(page.total, page.exportedRows), "Baixar");
    },
    run(format) {
      if (this.busy) return Promise.resolve(null);
      this.busy = true;
      this.clearTimer();
      this.notice = "";
      return this.confirmStart()
        .then((accepted) => (accepted ? this.collect() : null))
        .then((page) => {
          if (!page) return null;
          return this.confirmPartial(page).then((accepted) => (accepted ? page : null));
        })
        .then((page) => {
          if (!page) return null;
          const result = exportTable({
            screen: this.screen,
            table: this.title,
            format: format,
            columns: this.columns,
            rows: page.rows,
            envelope: page.envelope,
            total: page.total,
            truncated: page.truncated,
            filters: this.filters,
            period: this.period,
            link: browserLink(this.$route),
            generatedBy: browserUserName(),
            now: new Date(),
          });
          this.showNotice(exportNotice(result.exportedRows), false);
          this.$emit("exported", {
            format: result.format, fileName: result.fileName, exportedRows: result.exportedRows, total: result.total, truncated: result.truncated,
          });
          return result;
        })
        .catch((error) => {
          const message = exportErrorMessage(error, this.title);
          this.showNotice(message, true);
          this.$emit("error", { message: message });
          return null;
        })
        .then((result) => {
          this.busy = false;
          return result;
        });
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-export {
  position: relative;
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  margin-left: auto;
}

.pi-export__btn {
  white-space: nowrap;
}

.pi-export__btn[aria-disabled="true"] {
  opacity: 0.6;
  cursor: progress;
}

.pi-export__btn:focus,
.pi-export__item:focus {
  outline: 2px solid #1b59f8;
  outline-offset: 2px;
}

.pi-export__menu {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 40;
  min-width: 170px;
  margin: 4px 0 0;
  padding: 4px 0;
  border: 1px solid #d9dde3;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.14);
  list-style: none;
}

.pi-export__item {
  display: block;
  width: 100%;
  padding: 7px 12px;
  border: 0;
  background: #ffffff;
  color: #3a424e;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
}

.pi-export__item:hover,
.pi-export__item:focus {
  background: #eef3fe;
  color: #1340b5;
}

.pi-export__notice {
  max-width: 340px;
  font-size: 12px;
  color: #0f6b2b;
}

.pi-export__notice.is-error {
  color: #a3172b;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piExportMenu.vue